import { Audience } from '@/models/audiences';
import { useDeleteAudienceMutation, useSaveAudienceMutation, useUpdateAudienceMutation } from '@/services/endpoints/audiences';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';
import { DRAFT_NAME_PREFIX, FISCAL_CODE_TRAIT_ID } from './constants';

type Draft = {
  id: string;
  name: string;
  datasourceIds: string[];
  kept: boolean;
  /** False when the draft is an existing list reused as-is: never written, never deleted. */
  owned: boolean;
};

const sameIdSet = (a: string[], b: string[]) => [...a].sort().join('|') === [...b].sort().join('|');

/**
 * Manages the draft audience behind the composer. The estimate API needs a persisted
 * audience and its datasource links are immutable, so changing the selected lists means
 * delete + recreate. Unsaved drafts are deleted on unmount (same rationale as the
 * campaign wizard's SelectAudienceStep delete-on-abandon).
 */
export const useDraftCommunication = () => {
  const { id: workspaceId } = useWorkspace();
  const [saveAudience] = useSaveAudienceMutation();
  const [updateAudience] = useUpdateAudienceMutation();
  const [deleteAudience] = useDeleteAudienceMutation();
  const draftRef = useRef<Draft | null>(null);
  /** The creation in flight: concurrent callers for the same lists share it instead of each creating an audience. */
  const pendingRef = useRef<{ datasourceIds: string[]; promise: Promise<string> } | null>(null);

  /** Deletes the audience behind a draft, unless it is a reused list or the user kept it. */
  const dispose = (draft: Draft | null) => {
    if (draft && draft.owned && !draft.kept) {
      deleteAudience({ workspaceId, audienceId: draft.id });
    }
  };

  /**
   * `reuseAudienceId` is the audience of the list the user picked, passed by the composer when it
   * would otherwise recreate that list identically (one list, no filters). Reusing it is what makes
   * the send work: a brand new audience has no evaluated snapshot, and activating a campaign only
   * replays the snapshot, so nothing would go out.
   */
  const ensure = async (datasourceIds: string[], reuseAudienceId?: string): Promise<string> => {
    const current = draftRef.current;
    if (reuseAudienceId) {
      if (current?.id === reuseAudienceId) return reuseAudienceId;
      dispose(current);
      // a creation still in flight would land on top of the reused list: let it dispose itself instead
      pendingRef.current = null;
      draftRef.current = { id: reuseAudienceId, name: '', datasourceIds: [...datasourceIds], kept: true, owned: false };
      return reuseAudienceId;
    }
    if (current?.owned && sameIdSet(current.datasourceIds, datasourceIds)) return current.id;
    const pending = pendingRef.current;
    if (pending && sameIdSet(pending.datasourceIds, datasourceIds)) return pending.promise;
    dispose(current);
    // the ref must not keep pointing at a deleted audience while the replacement is in flight
    draftRef.current = null;
    const name = `${DRAFT_NAME_PREFIX}${DateTime.now().toFormat('dd/LL/yyyy HH:mm')}`;
    const promise = saveAudience({
      workspaceId,
      audience: { name, matchingTrait: FISCAL_CODE_TRAIT_ID, datasourceIds },
    })
      .unwrap()
      .then(
        (audience) => {
          const created: Draft = { id: audience.id, name, datasourceIds: [...datasourceIds], kept: false, owned: true };
          if (pendingRef.current?.promise !== promise) {
            // the lists changed while this one was in flight: a leftover, the caller follows the newer draft
            dispose(created);
            const replacement = pendingRef.current?.promise ?? draftRef.current?.id;
            // no replacement left (the newer creation failed): fail too rather than hand out the deleted id
            if (replacement === undefined) throw new Error('draft creation superseded');
            return replacement;
          }
          pendingRef.current = null;
          draftRef.current = created;
          return audience.id;
        },
        (error: unknown) => {
          // a failed creation must not be handed to the next caller: let it retry
          if (pendingRef.current?.promise === promise) pendingRef.current = null;
          throw error;
        }
      );
    pendingRef.current = { datasourceIds, promise };
    return promise;
  };

  /**
   * Writes expression (and optional display name) to the current draft audience. A reused list is
   * left untouched: it already carries that expression, and it is the user's list, not a draft.
   * Does not mark the draft as retained — abandon cleanup still deletes it unless
   * `retain()` was called (save-as-list) or `release()` detached it after campaigns.
   */
  const persist = async (expression: Audience['expression'], name?: string) => {
    const current = draftRef.current;
    if (!current || !current.owned) return;
    const nextName = name ?? current.name;
    await updateAudience({ workspaceId, audienceId: current.id, audience: { name: nextName, expression } }).unwrap();
    draftRef.current = { ...current, name: nextName };
  };

  /** Keep the current draft on unmount (e.g. user explicitly saved it as a named list). */
  const retain = () => {
    const current = draftRef.current;
    if (!current || current.kept) return;
    draftRef.current = { ...current, kept: true };
  };

  // dispose reads draftRef at unmount time, so the empty dep list is the whole point here
  useEffect(() => () => dispose(draftRef.current), []);

  /**
   * Detaches the hook from the current draft without touching the audience itself: the campaigns
   * just created from it keep referencing it untouched, and the next `ensure()` call is forced to
   * create a brand new audience (no more `sameIdSet` reuse of an already-referenced one, which
   * would otherwise let a later `persist()` overwrite the expression campaigns already depend on).
   */
  const release = () => {
    draftRef.current = null;
  };

  // One object per workspace: the context derives its actions from it, and the geo panel runs
  // `estimateZone` from an effect, so a fresh object per render would re-trigger that effect
  // on every store update (each estimate response included) and loop.
  return useMemo(
    () => ({
      ensure,
      persist,
      retain,
      release,
      currentId: () => draftRef.current?.id ?? null,
      currentName: () => draftRef.current?.name ?? null,
    }),
    [workspaceId, saveAudience, updateAudience, deleteAudience]
  );
};
